import React from "react";
import styled from "@emotion/styled";

const WorkflowPanel = ({ children }) => (
  <Panel>{children}</Panel>
);

export const WorkflowHeader = ({ tone, title, subtitle, icon }) => (
  <Header $tone={tone}>
    {icon && <HeaderIcon>{icon}</HeaderIcon>}
    <HeaderText>{title}</HeaderText>
    {subtitle && <HeaderMeta>{subtitle}</HeaderMeta>}
  </Header>
);

export const WorkflowBody = ({ children, className }) => (
  <Body className={className}>{children}</Body>
);

export const WorkflowScroll = ({ children, className }) => (
  <Scroll className={className}>{children}</Scroll>
);

export const WorkflowFooter = ({ children }) => (
  <Footer>{children}</Footer>
);

export const DecisionCard = ({ children, tone }) => (
  <DecisionSurface $tone={tone}>{children}</DecisionSurface>
);

export const DecisionTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 1.35rem;
  font-weight: 800;
  text-align: center;
`);

export const DecisionCopy = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.45;
  text-align: center;
`);

export const FieldCard = styled.section(({ theme }) => `
  min-width: 0;
  display: grid;
  gap: 0.85rem;
  padding: 1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
`);

const Panel = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: ${theme.palette.common.cardBackground};
  overflow: hidden;
`);

const Header = styled.header(({ theme, $tone }) => {
  const isSuccess = $tone === "success";
  const isError = $tone === "error";
  const isDanger = $tone === "danger";
  const toneColor = isSuccess
    ? theme.palette.primary.dark
    : isError || isDanger
      ? theme.palette.common.redDark
      : theme.palette.common.grayText;
  const toneBackground = isSuccess
    ? theme.palette.primary.labelTransparent
    : isError || isDanger
      ? theme.palette.common.redTransparent
      : theme.palette.common.white;

  return `
    min-height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    padding: 0.8rem 1rem;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
    background: ${toneBackground};
    color: ${toneColor};
  `;
});

const HeaderIcon = styled.div(({ theme }) => `
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  background: ${theme.palette.common.white};
  border: 0.1rem solid currentColor;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`);

const HeaderText = styled.span`
  color: inherit;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  font-family: -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", 'Inter', "Helvetica Neue", sans-serif;
`;

const HeaderMeta = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.9rem;
  font-weight: 600;
`);

const Body = styled.div(({ theme }) => `
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  background: ${theme.palette.common.grayBorderLight};
  overflow: hidden;
`);

const Scroll = styled.div(({ theme }) => `
  min-height: 0;
  height: 100%;
  max-height: 100%;
  display: grid;
  align-content: start;
  gap: 1rem;
  padding: 1rem;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${theme.palette.common.cardBackground};

  &.centered {
    align-content: center;
  }

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:vertical {
    width: 0.4rem;
  }
`);

const Footer = styled.footer(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  padding: 0.9rem 1rem;
  border-top: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`);

const DecisionSurface = styled.div(({ theme, $tone }) => {
  const accent = $tone === "danger" ? theme.palette.common.redDark : theme.palette.primary.dark;

  return `
    min-width: 0;
    display: grid;
    justify-items: center;
    gap: 0.75rem;
    padding: 2rem;
    border: 0.1rem solid ${theme.palette.common.grayBorder};
    border-left: 0.35rem solid ${accent};
    border-radius: 0.8rem;
    background: ${theme.palette.common.white};
  `;
});

export default WorkflowPanel;
